import {
    IElementBaseModel,
} from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import {
    ISlideModel,
    SlideModelSdkTypeV3,
} from '@src/slide/domain/ui-model/element-modeles/Interface/slide-model.interface';
import { FOOTER, NUMBER, DATE } from '@src/slide/domain/ui-model/constant/place-holder';
import { transaction } from '@src/slide/domain/slide-app';
import { ISlideFooter, IFooterProps, DatetimeType } from '@src/slide/domain/ui-model/element-modeles/property/slide-footer/slide-footer.interface';
import unset from 'lodash/unset';
import getCurrentFormatTime, { DatetimeFieldType } from '../text/utils/format-datetime';
import { IShapeBaseModel, IShapeModel } from '../../Interface/shape-model.interface';
import { IParagraph } from '../text/interface/editor-para.interface';
import { IPcEditorAppServices } from '@src/slide/application/entry/toc/editor/pc/pc-editor-app-services.interface';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import i18n from '@i18next/i18n';
import { IShapeData, ISlideDataModel, STPlaceholderType, IShapeDataModel } from '@tencent/slide-sdk-model';
import { ESpecialOperationType } from '@src/slide/application/service/selection/selected-with-commit-manager.interface';

export class SlideFooter implements ISlideFooter {
    private model: ISlideModel;

    constructor(model: ISlideModel) {
        this.model = model;
    }

    /**
     * 侧边栏页脚元素勾选回调
     * @param props
     */
    public updatePlaceholderFormat(props: IFooterProps): void {
        const { value, checked, targetShape } = props;
        transaction(() => {
            this.handlePlCheckedChange(checked, value, targetShape);
        });
        this.model.selectionService?.selectedElements.clear();
    }

    public updateDatetimeType(value: DatetimeType): void {
        const datetimePl = this.model.children.find(child => child.isDatePl);
        if (!datetimePl) {
            return;
        }
        let paragraphList;
        if (value === DatetimeType.FIXED) {
            paragraphList = this.getFixedDatetimeParagraphList();
        } else {
            paragraphList = this.getAutoUpdateDatetimeParagraphList(DatetimeFieldType.DATETIME_1);
        }
        (datetimePl as IShapeBaseModel).updateParagraphList(paragraphList, {});
    }

    public getDatetimeFieldType() {
        const datetimePl = this.model.children.find(child => child.isDatePl);
        let datetimeFieldType = '';
        (datetimePl as IShapeModel)?.paragraphList?.forEach((para) => {
            para.textRuns?.forEach((run) => {
                if (run.fieldType) {
                    datetimeFieldType = run.fieldType;
                }
            });
        });
        return datetimeFieldType;
    }

    public updateDatetimeFieldType(type: DatetimeFieldType) {
        const datetimePl = this.model.children.find(child => child.isDatePl);
        if (!datetimePl) {
            return;
        }
        const paragraphList = this.getAutoUpdateDatetimeParagraphList(type);
        (datetimePl as IShapeBaseModel).updateParagraphList(paragraphList, {});
    }

    public updateSlideFooterContent(value: string): void {
        const footer = this.model.children.find(child => child.isFooterPl);
        const paragraphList = [{
            textRuns: [{
                text: value,
            }],
        }];
        (footer as IShapeBaseModel)?.updateParagraphList(paragraphList, {});
    }

    public updateDatetimeContent(value: string): void {
        const footer = this.model?.children.find(child => child.isDatePl);
        const paragraphList = [{
            textRuns: [{
                text: value,
            }],
        }];
        (footer as IShapeBaseModel)?.updateParagraphList(paragraphList, {});
    }

    public getCurrentDatetimeText(): string {
        const footer = this.model?.children.find(child => child.isDatePl);
        let text = '';
        (footer as IShapeModel)?.paragraphList?.forEach((para) => {
            para.textRuns?.forEach((run) => {
                text = `${text}${run.text}`;
            });
        });
        return text;
    }

    public getCurrentSlideFooterContent(): string {
        const footer = this.model?.children.find(child => child.isFooterPl);
        let text = '';
        (footer as IShapeModel)?.paragraphList?.forEach((para) => {
            para.textRuns?.forEach((run) => {
                text = `${text}${run.text}`;
            });
        });
        return text;
    }

    public getDatetimeType(): DatetimeType {
        const datetimePl = this.model?.children.find(child => child.isDatePl);
        if (!datetimePl) {
            return DatetimeType.AUTO_UPDATE;
        }
        let datetimeType = DatetimeType.FIXED;
        (datetimePl as IShapeModel)?.paragraphList?.forEach((para) => {
            para.textRuns?.forEach((run) => {
                if (run.fieldType) {
                    datetimeType = DatetimeType.AUTO_UPDATE;
                }
            });
        });
        return datetimeType;
    }

    public getDatetimeParagraphList(): IParagraph[]|undefined {
        const datetimePl = this.model?.children.find(child => child.isDatePl);
        if (!datetimePl) {
            return [];
        }
        const datetimeType = this.getDatetimeType();
        if (datetimeType === DatetimeType.FIXED) {
            return (datetimePl as IShapeModel).paragraphList;
        }
        let fieldRun;
        (datetimePl as IShapeModel)?.paragraphList?.forEach((para) => {
            para.textRuns?.forEach((run) => {
                if (run.fieldType) {
                    fieldRun = run;
                }
            });
        });
        return [{ textRuns: [fieldRun] }];
    }

    private getFixedDatetimeParagraphList(): IParagraph[]|undefined {
        const lang = this.getLanguageForTextRun();
        const timeText = this.getCurrentDatetimeText();
        return [{
            textRuns: [{
                fieldId: undefined,
                fieldType: undefined,
                text: timeText,
                type: 'run',
                styles: { lang },
            }],
        }];
    }

    private getAutoUpdateDatetimeParagraphList(type: DatetimeFieldType): IParagraph[]|undefined {
        const lang = this.getLanguageForTextRun();
        const datetime = this.getAppService().getInfrastructureService().initDatetime;
        const timeText = getCurrentFormatTime(type.slice(type.indexOf('datetime') + 8), lang, datetime);
        return [{
            textRuns: [{
                fieldType: type,
                fieldId: undefined,
                type: 'field',
                // 这个text在slide下没有作用，自动更新类型会忽视text原本是什么，而在master和layout上会显示这个text
                text: timeText,
                // @ts-ignore
                styles: { lang, 'raw-lang': true },
            }],
        }];
    }

    private getPlaceholderType(type: string): STPlaceholderType |undefined {
        let plType;
        if (type === NUMBER) {
            plType = STPlaceholderType.sldNum;
        } else if (type === FOOTER) {
            plType = STPlaceholderType.ftr;
        } else if (type === DATE) {
            plType = STPlaceholderType.dt;
        }
        return plType;
    }

    private handlePlCheckedChange(checked: boolean, type: string, targetShape?: IElementBaseModel) {
        const plType = this.getPlaceholderType(type);
        if (!plType) {
            return;
        }
        const plShape = this.model.findChildByPlTypeNum(plType);
        this.model.selectionService?.selectedWithCommit.markSpecialOperate(ESpecialOperationType.keep);
        if (checked) {
            if (plShape) {
                return;
            }
            const shapes = this.model.getRelationLayoutModel()?.sdkData?.shapeList;
            const [layoutPl] = shapes?.filter(shape => shape.props.nvPr?.ph?.type === plType) || [];
            if (layoutPl) {
                const layoutPlSdkData: IShapeData = (layoutPl as IShapeDataModel).getCloneData();
                unset(layoutPlSdkData, 'spPr.xfrm');
                this.initDatetimeData(layoutPlSdkData, plType);
                this.handleChecked(layoutPlSdkData);
            }
        } else {
            const plSdkData = targetShape?.sdkData || plShape?.sdkData;
            this.handleUnChecked(plSdkData);
        }
    }

    private initDatetimeData(layoutPlSdkData: IShapeData, plType) {
        if (plType !== STPlaceholderType.dt) {
            return;
        }
        (layoutPlSdkData as IShapeData).txBody?.p?.forEach((para) => {
            para.egTextRun?.forEach((item) => {
                if (item.fld?.type === DatetimeFieldType.DATETIME_FIGUREOUT) {
                    item.fld.type = DatetimeFieldType.DATETIME_1;
                }
            });
        });
    }

    private handleChecked(plSdkData: IShapeData) {
        if (!plSdkData) return;
        (this.model.sdkData as SlideModelSdkTypeV3)?.api.addShapes([plSdkData as IShapeData]);
    }

    private handleUnChecked(plSdkData: IShapeData) {
        if (!plSdkData) return;
        this.deleteShapes([plSdkData]);
    }

    private getAppService(): IPcEditorAppServices {
        return AppServiceProvider.getInstance().getAppServices() as IPcEditorAppServices;
    }

    private getLanguageForTextRun() {
        const { language } = i18n;
        let lan;
        if (language === 'zh-CN' || language === 'zh-HK' || language === 'zh') {
            lan = 'ea';
        } else {
            lan = 'latin';
        }
        return lan;
    }

    private deleteShapes(deleteElements): void {
        if (!this.model.sdkData) {
            return;
        }
        (this.model.sdkData as unknown as ISlideDataModel).api.deleteShapes(deleteElements);
    }
}
